export type BannerMode = 'static' | 'enter';
export type BannerFrom = 'left' | 'right' | 'top' | 'bottom' | 'center';
export type BannerLayout = 'carousel' | 'simple';

export type BannerDisplayOptions = {
  mode: BannerMode;
  from: BannerFrom;
  /** Seconds the banner stays on screen (enter mode). */
  holdSec: number;
  /** Seconds hidden before the next appearance (enter mode). */
  everySec: number;
  layout: BannerLayout;
  /** Custom text; empty = default copy. Supports {code} and {days}. Carousel: split slides with `|`. */
  text: string;
  bg: string;
  fg: string;
  accent: string;
  /** Orbs / glow / scan effects. */
  fx: boolean;
};

export const BANNER_FROM_OPTIONS: { value: BannerFrom; label: string }[] = [
  { value: 'left', label: 'Слева' },
  { value: 'right', label: 'Справа' },
  { value: 'top', label: 'Сверху' },
  { value: 'bottom', label: 'Снизу' },
  { value: 'center', label: 'По центру' },
];

export const BANNER_LAYOUT_OPTIONS: { value: BannerLayout; label: string }[] = [
  { value: 'carousel', label: 'Карусель' },
  { value: 'simple', label: 'Одна строка' },
];

export const DEFAULT_BANNER_OPTIONS: BannerDisplayOptions = {
  mode: 'enter',
  from: 'right',
  holdSec: 30,
  everySec: 60,
  layout: 'carousel',
  text: '',
  bg: '#1a0b2e',
  fg: '#f5f0ff',
  accent: '#c4b5fd',
  fx: true,
};

export type BannerThemePreset = {
  id: string;
  label: string;
  bg: string;
  fg: string;
  accent: string;
  fx: boolean;
};

export const BANNER_THEME_PRESETS: BannerThemePreset[] = [
  {
    id: 'violet',
    label: 'Фиолет',
    bg: '#1a0b2e',
    fg: '#f5f0ff',
    accent: '#c4b5fd',
    fx: true,
  },
  {
    id: 'ocean',
    label: 'Океан',
    bg: '#0a1628',
    fg: '#e8f4ff',
    accent: '#38bdf8',
    fx: true,
  },
  {
    id: 'ember',
    label: 'Уголь',
    bg: '#141414',
    fg: '#fafafa',
    accent: '#f97316',
    fx: false,
  },
];

export const DEFAULT_SIMPLE_BANNER_TEXT =
  'Получи +{days} дней при использовании промокода';

/** Slide separator for carousel custom text (not comma — phrases often contain commas). */
export const BANNER_SLIDE_SEPARATOR = '|';

const STORAGE_KEY = 'yp_banner_display_opts';
const TEXT_MAX = 360;

function clampInt(raw: unknown, min: number, max: number, fallback: number): number {
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

function normalizeHex(raw: unknown, fallback: string): string {
  const s = String(raw ?? '')
    .trim()
    .replace(/^#/, '');
  if (/^[0-9a-fA-F]{6}$/.test(s)) return `#${s.toLowerCase()}`;
  if (/^[0-9a-fA-F]{3}$/.test(s)) {
    const expanded = s
      .split('')
      .map((c) => c + c)
      .join('');
    return `#${expanded.toLowerCase()}`;
  }
  return fallback;
}

function hexForQuery(hex: string): string {
  return hex.replace(/^#/, '').toLowerCase();
}

export function normalizeBannerOptions(
  partial?: Partial<BannerDisplayOptions> | null,
): BannerDisplayOptions {
  const mode = partial?.mode === 'static' || partial?.mode === 'enter' ? partial.mode : DEFAULT_BANNER_OPTIONS.mode;
  const from = BANNER_FROM_OPTIONS.some((o) => o.value === partial?.from)
    ? (partial!.from as BannerFrom)
    : DEFAULT_BANNER_OPTIONS.from;
  const layout =
    partial?.layout === 'simple' || partial?.layout === 'carousel'
      ? partial.layout
      : DEFAULT_BANNER_OPTIONS.layout;
  const textRaw = typeof partial?.text === 'string' ? partial.text : DEFAULT_BANNER_OPTIONS.text;
  // Storage/URL form: collapse newlines to `|`, squeeze spaces, trim ends.
  // Live typing must NOT go through this path on every keystroke (see updateBannerOpts).
  const text = textRaw
    .replace(/\r\n|\r|\n/g, ' | ')
    .replace(/[ \t]+/g, ' ')
    .trim()
    .slice(0, TEXT_MAX);
  let fx = DEFAULT_BANNER_OPTIONS.fx;
  if (typeof partial?.fx === 'boolean') {
    fx = partial.fx;
  }

  return {
    mode,
    from,
    holdSec: clampInt(partial?.holdSec, 5, 300, DEFAULT_BANNER_OPTIONS.holdSec),
    everySec: clampInt(partial?.everySec, 3, 600, DEFAULT_BANNER_OPTIONS.everySec),
    layout,
    text,
    bg: normalizeHex(partial?.bg, DEFAULT_BANNER_OPTIONS.bg),
    fg: normalizeHex(partial?.fg, DEFAULT_BANNER_OPTIONS.fg),
    accent: normalizeHex(partial?.accent, DEFAULT_BANNER_OPTIONS.accent),
    fx,
  };
}

export function readBannerOptions(): BannerDisplayOptions {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_BANNER_OPTIONS };
    return normalizeBannerOptions(JSON.parse(raw) as Partial<BannerDisplayOptions>);
  } catch {
    return { ...DEFAULT_BANNER_OPTIONS };
  }
}

export function writeBannerOptions(opts: BannerDisplayOptions): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizeBannerOptions(opts)));
  } catch {
    /* ignore */
  }
}

export function parseBannerOptionsFromSearch(search: string): BannerDisplayOptions {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const modeRaw = params.get('mode');
  const fromRaw = params.get('from');
  const layoutRaw = params.get('layout');
  const fxRaw = params.get('fx');
  let fx: boolean | undefined;
  if (fxRaw === '0' || fxRaw === 'false') fx = false;
  else if (fxRaw === '1' || fxRaw === 'true') fx = true;

  return normalizeBannerOptions({
    mode: modeRaw === 'static' || modeRaw === 'enter' ? modeRaw : undefined,
    from: BANNER_FROM_OPTIONS.some((o) => o.value === fromRaw) ? (fromRaw as BannerFrom) : undefined,
    holdSec: params.has('hold') ? Number(params.get('hold')) : undefined,
    everySec: params.has('every') ? Number(params.get('every')) : undefined,
    layout: layoutRaw === 'simple' || layoutRaw === 'carousel' ? layoutRaw : undefined,
    text: params.has('text') ? params.get('text') ?? '' : undefined,
    bg: params.get('bg') ?? undefined,
    fg: params.get('fg') ?? undefined,
    accent: params.get('accent') ?? undefined,
    fx,
  });
}

export function bannerOptionsToSearch(opts: BannerDisplayOptions): string {
  const o = normalizeBannerOptions(opts);
  const params = new URLSearchParams();
  if (o.mode === 'static') {
    params.set('mode', 'static');
  } else {
    params.set('mode', 'enter');
    params.set('from', o.from);
    params.set('hold', String(o.holdSec));
    params.set('every', String(o.everySec));
  }
  if (o.layout !== DEFAULT_BANNER_OPTIONS.layout) params.set('layout', o.layout);
  if (o.text) params.set('text', o.text);
  if (o.bg !== DEFAULT_BANNER_OPTIONS.bg) params.set('bg', hexForQuery(o.bg));
  if (o.fg !== DEFAULT_BANNER_OPTIONS.fg) params.set('fg', hexForQuery(o.fg));
  if (o.accent !== DEFAULT_BANNER_OPTIONS.accent) params.set('accent', hexForQuery(o.accent));
  if (!o.fx) params.set('fx', '0');
  return params.toString();
}

export function withBannerOptions(url: string, opts: BannerDisplayOptions): string {
  try {
    const u = new URL(url);
    u.search = '';
    const qs = bannerOptionsToSearch(opts);
    return qs ? `${u.toString()}?${qs}` : u.toString();
  } catch {
    const base = url.split('?')[0];
    const qs = bannerOptionsToSearch(opts);
    return qs ? `${base}?${qs}` : base;
  }
}

export function applyBannerTextTemplate(
  template: string,
  vars: { code: string; days: number },
): string {
  const base = (template || DEFAULT_SIMPLE_BANNER_TEXT).trim() || DEFAULT_SIMPLE_BANNER_TEXT;
  return base
    .replaceAll('{code}', vars.code)
    .replaceAll('{days}', String(vars.days));
}

/**
 * Split custom carousel text into slides.
 * Prefer ` | ` between phrases; newlines also work. Do not use commas as separators.
 */
export function splitBannerCarouselSlides(raw: string): string[] {
  const text = String(raw ?? '').trim();
  if (!text) return [];
  const parts = text
    .split(/\s*\|\s*|\n+/)
    .map((p) => p.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
  return parts.slice(0, 8);
}

export function bannerCssVars(opts: BannerDisplayOptions): Record<string, string> {
  const o = normalizeBannerOptions(opts);
  return {
    '--banner-bg': o.bg,
    '--banner-fg': o.fg,
    '--banner-accent': o.accent,
  };
}

/** Merge server prefs over local when hydrating cabinet. */
export function mergeBannerOptions(
  local: BannerDisplayOptions,
  server: Partial<BannerDisplayOptions> | null | undefined,
  preferServer: boolean,
): BannerDisplayOptions {
  if (!server || Object.keys(server).length === 0) return normalizeBannerOptions(local);
  if (preferServer) return normalizeBannerOptions({ ...local, ...server });
  return normalizeBannerOptions({ ...server, ...local });
}

export function bannerOptionsEqual(a: BannerDisplayOptions, b: BannerDisplayOptions): boolean {
  const x = normalizeBannerOptions(a);
  const y = normalizeBannerOptions(b);
  return (
    x.mode === y.mode &&
    x.from === y.from &&
    x.holdSec === y.holdSec &&
    x.everySec === y.everySec &&
    x.layout === y.layout &&
    x.text === y.text &&
    x.bg === y.bg &&
    x.fg === y.fg &&
    x.accent === y.accent &&
    x.fx === y.fx
  );
}
